import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PermissionService } from '@core/services/permission.service';
import { DateTimePickerComponent } from '@shared/components/date-time-picker/date-time-picker.component';
import { LookupStore } from '@core/lookup/lookup.store';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';
import { Permission } from '@shared/enums/permission.enum';
import { InterventionStore } from '../../../intervention.store';
import {
  InterventionSessionAttendanceRequest,
  InterventionSessionRequest,
  InterventionSessionResponse,
} from '../../../intervention.model';
import { nullIfBlank } from '@shared/utils/payload-utils';

// The sessions delivered for an intervention (MW-139). Scheduling needs the sessions.record permission,
// which affiliates hold for interventions they have accepted; the server enforces who may do what, so
// this panel only hides what the caller can't use.
@Component({
  selector: 'intervention-sessions-panel',
  imports: [DatePipe, RouterLink, ReactiveFormsModule, DateTimePickerComponent, ...SharedMaterialUIModule],
  templateUrl: './intervention-sessions-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InterventionSessionsPanelComponent {
  protected readonly interventionStore = inject(InterventionStore);
  protected readonly lookupStore = inject(LookupStore);
  private readonly permissionService = inject(PermissionService);
  private readonly formBuilder = inject(FormBuilder);

  readonly interventionId = input.required<string>();
  readonly interventionStatus = input<string | null>(null);
  // Formal EAP counselling types record clinical notes against each attended session (MW-162).
  readonly usesSessionNotes = input(false);

  // The session being edited; null while scheduling a new one. The form is hidden unless formOpen.
  protected readonly editingSessionId = signal<string | null>(null);
  protected readonly formOpen = signal(false);

  protected readonly form = this.formBuilder.nonNullable.group({
    sessionDate: ['', Validators.required],
    durationMinutes: this.formBuilder.control<number | null>(null, [Validators.min(1), Validators.max(600)]),
    counsellingMediumId: [''],
    location: [''],
    notes: [''],
  });

  // The session whose attendance is being recorded; the form is shown under that session's row.
  protected readonly attendanceSessionId = signal<string | null>(null);

  protected readonly attendanceForm = this.formBuilder.nonNullable.group({
    attended: [true],
    checkInTime: [''],
    checkOutTime: [''],
    noShowReason: [''],
  });

  protected readonly mediums = computed(() => this.lookupStore.items(LOOKUP_CODES.counsellingMedium));

  protected readonly canRecord = computed(
    () => this.permissionService.hasPermission(Permission.SessionsRecord) && this.interventionStatus() !== 'Closed',
  );

  // Verifying is the internal check on what a provider reports, so it needs general intervention-edit rights,
  // which affiliates do not have.
  protected hasNote(sessionId: string): boolean {
    return this.interventionStore.sessionNotes().some((note) => note.sessionId === sessionId);
  }

  protected readonly canVerify = computed(
    () => this.permissionService.hasPermission(Permission.InterventionsUpdate) && this.interventionStatus() !== 'Closed',
  );

  // BR005: service consent is recorded (on the Consent tab) before a session can be scheduled.
  protected readonly hasServiceConsent = computed(() => this.interventionStore.hasConsent('Service'));

  protected readonly activeCount = computed(
    () => this.interventionStore.sessions().filter((session) => session.status !== 'Cancelled').length,
  );

  constructor() {
    this.lookupStore.load([LOOKUP_CODES.counsellingMedium]);
    effect(() => this.interventionStore.loadSessions(this.interventionId()));
    effect(() => {
      if (this.usesSessionNotes()) {
        this.interventionStore.loadSessionNotes(this.interventionId());
      }
    });
  }

  protected openNew(): void {
    this.editingSessionId.set(null);
    this.form.reset({ sessionDate: '', durationMinutes: null, counsellingMediumId: '', location: '', notes: '' });
    this.formOpen.set(true);
  }

  protected openEdit(session: InterventionSessionResponse): void {
    this.editingSessionId.set(session.id);
    this.form.reset({
      sessionDate: session.sessionDate.slice(0, 16),
      durationMinutes: session.durationMinutes,
      counsellingMediumId: session.counsellingMediumId ?? '',
      location: session.location ?? '',
      notes: session.notes ?? '',
    });
    this.formOpen.set(true);
  }

  protected closeForm(): void {
    this.formOpen.set(false);
    this.editingSessionId.set(null);
  }

  protected save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    // Wall-clock time with no timezone, so the time shown back is the time that was entered.
    const request: InterventionSessionRequest = {
      affiliateId: null,
      sessionDate: `${value.sessionDate}:00`,
      durationMinutes: value.durationMinutes,
      counsellingMediumId: value.counsellingMediumId || null,
      location: nullIfBlank(value.location),
      notes: nullIfBlank(value.notes),
    };

    this.interventionStore.saveSession(this.interventionId(), this.editingSessionId(), request, () => this.closeForm());
  }

  protected openAttendance(session: InterventionSessionResponse): void {
    this.attendanceForm.reset({
      attended: session.status !== 'NoShow',
      checkInTime: session.checkInTime?.slice(0, 16) ?? '',
      checkOutTime: session.checkOutTime?.slice(0, 16) ?? '',
      noShowReason: session.noShowReason ?? '',
    });
    this.attendanceSessionId.set(session.id);
  }

  protected closeAttendance(): void {
    this.attendanceSessionId.set(null);
  }

  protected saveAttendance(): void {
    const sessionId = this.attendanceSessionId();
    const value = this.attendanceForm.getRawValue();
    if (!sessionId || (!value.attended && !value.noShowReason.trim())) {
      this.attendanceForm.controls.noShowReason.markAsTouched();
      return;
    }

    // Wall-clock times with no timezone, like the session date.
    const request: InterventionSessionAttendanceRequest = value.attended
      ? {
          attended: true,
          checkInTime: value.checkInTime ? `${value.checkInTime}:00` : null,
          checkOutTime: value.checkOutTime ? `${value.checkOutTime}:00` : null,
          noShowReason: null,
        }
      : { attended: false, checkInTime: null, checkOutTime: null, noShowReason: value.noShowReason.trim() };

    this.interventionStore.captureAttendance(this.interventionId(), sessionId, request, () => this.closeAttendance());
  }

  protected verify(session: InterventionSessionResponse): void {
    this.interventionStore.verifyAttendance(this.interventionId(), session.id);
  }

  protected cancel(session: InterventionSessionResponse): void {
    this.interventionStore.cancelSession(this.interventionId(), session.id);
  }

  protected statusLabel(status: string): string {
    return status === 'NoShow' ? 'No show' : status;
  }
}
